"use client";

import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Pagination } from "@/components/portal/common/DataTable/Pagination";
import {
  useEmailNotifications,
  useMarkEmailNotificationOpened,
} from "@/hooks/notifications/useEmailNotifications";
import { NOTIFICATION_DEFAULT_PAGE_SIZE } from "@/services/notifications";
import type { EmailNotificationItem } from "@/types/api/notifications";
import { ArrowRight, ExternalLink, Mail, MailOpen } from "lucide-react";
import { Skeleton } from "@/components/common/Skeleton";
import { formatKLDateTime } from "@/lib/dateHelpers";
import { isInAppPath } from "@/lib/notificationUrl";

interface EmailNotificationsModalProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * `sent_at` comes back timezone-naive (`2026-08-18 04:32:41`), so
 * `new Date(...)` used to read it as the browser's LOCAL time and every
 * notification displayed 8 hours early (QA #65). formatKLDateTime appends the
 * missing 'Z' and pins the zone to Asia/Kuala_Lumpur, the way the rest of the
 * app already formats API timestamps.
 */
const formatTimestamp = (iso: string | null) => formatKLDateTime(iso, "");

const formatEvent = (event: string) =>
  event
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const EmailNotificationsModal = ({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: EmailNotificationsModalProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(NOTIFICATION_DEFAULT_PAGE_SIZE);

  const { data, isLoading, isError, error } = useEmailNotifications(
    { page, per_page: pageSize },
    { enabled: open }
  );

  const markOpened = useMarkEmailNotificationOpened();

  const items = data?.data?.items ?? [];
  const totalPages = data?.meta?.last_page ?? 1;

  const handleOpenItem = (item: EmailNotificationItem) => {
    if (!item.opened_at) {
      markOpened.mutate(item.id);
    }

    if (!item.content_url) return;

    // A relative path is an in-app destination (see the LinksToAppPage contract
    // on the backend): route to it and close the modal, rather than opening a
    // second tab on our own origin. Anything absolute stays a new-tab external
    // link, which is what content_url meant before it carried app routes.
    if (isInAppPath(item.content_url)) {
      setOpen(false);
      navigate(item.content_url);
      return;
    }

    window.open(item.content_url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[640px] bg-white rounded-xl shadow-2xl p-0 gap-0 overflow-hidden max-h-[85vh] flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-gray-100">
          <DialogTitle className="text-lg font-bold text-[#05004E]">
            Email Notifications
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2 px-6 py-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="px-6 py-10 text-center text-sm text-red-500">
              {(error as Error)?.message || "Failed to load notifications."}
            </div>
          ) : items.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-gray-500">
              <Mail className="w-8 h-8 mx-auto mb-3 text-gray-300" />
              No email notifications yet.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((item) => {
                const unread = !item.opened_at;
                return (
                  <li
                    key={item.id}
                    onClick={() => handleOpenItem(item)}
                    className={`px-6 py-4 cursor-pointer transition-colors hover:bg-gray-50 ${
                      unread ? "bg-[#3C32950A]" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0">
                        {unread ? (
                          <Mail className="w-5 h-5 text-[#3C3295]" />
                        ) : (
                          <MailOpen className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <p
                            className={`text-sm truncate ${
                              unread
                                ? "font-bold text-[#05004E]"
                                : "font-medium text-[#1D293D]"
                            }`}
                          >
                            {item.subject}
                          </p>
                          <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                            {formatTimestamp(item.sent_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[#64748B] mt-1">
                          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-[#3C3295] font-medium">
                            {formatEvent(item.email_event)}
                          </span>
                          <span className="truncate">
                            From {item.sender_email}
                          </span>
                        </div>
                        {item.content_url && (
                          <p className="text-xs text-[#3C3295] mt-1.5 inline-flex items-center gap-1">
                            {isInAppPath(item.content_url) ? (
                              <>
                                <ArrowRight className="w-3 h-3" />
                                Go to related page
                              </>
                            ) : (
                              <>
                                <ExternalLink className="w-3 h-3" />
                                View email content
                              </>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-gray-100">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
